import { clsx } from "clsx";
import type { FC, ReactElement } from "react";
import { type BaseFieldProps, Field, type WrappedFieldProps } from "redux-form";

import { fieldIsInWarnState, fieldIsInvalid } from "../../../app-utils/form.js";
import type { ColumnAnnotation } from "../../../graphql.js";
import { ConditionsSelector } from "../../analytical-viewer/ConditionSelector.js";
import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.js";

export interface IConditionSelectorFieldProps extends Partial<WrappedFieldProps> {
  // input?: any; // injected as WrappedFieldProps
  // meta?: any; // injected as WrappedFieldProps

  formGroupClassName?: string;
  title?: string | ReactElement;
  options?: ColumnAnnotation[];
  hotspotId?: string;
  tooltip?: string;
  hidden?: boolean;
  label?: string;
  addOptionMessage?: string;
  limit?: number;
  limitReachedMessage?: string;
  hideConditionSortDialogButton?: boolean;
  formControlClasses?: string;

  handleChange?: (e) => void;
  horizontalLayout?: boolean;
}

/**
 * Condition Selector field to be used in context of redux-form
 */
const ConditionSelectorField: FC<IConditionSelectorFieldProps> = (field) => {
  const selectClasses = clsx({ error: fieldIsInvalid(field) });

  return (
    <FormInputGroup horizontalLayout={field.horizontalLayout}>
      {field.title && <FormLabel hotspotId={field.hotspotId}>{field.title}</FormLabel>}

      <FormInputWidget>
        <ConditionsSelector
          label={field.label}
          wizardStep={null}
          annotations={field.options}
          timeSeriesEnabled={false}
          selectedAnnotations={field.input.value}
          addOptionMessage={field.addOptionMessage}
          limit={field.limit}
          limitReachedMessage={field.limitReachedMessage}
          tooltip={field.tooltip}
          selectClasses={selectClasses}
          formControlClasses={field.formControlClasses}
          hideConditionSortDialogButton={field.hideConditionSortDialogButton}
          noCallout
          onChange={(e) => {
            field.input.onChange(e);
            if (field.handleChange) {
              field.handleChange(e);
            }
          }}
        />
        {fieldIsInvalid(field) && <FormError>{field.meta.error}</FormError>}
        {fieldIsInWarnState(field) && !fieldIsInvalid(field) && <FormWarning>{field.meta.warning}</FormWarning>}
      </FormInputWidget>
    </FormInputGroup>
  );
};

export default ConditionSelectorField;

export function ConditionSelectorReduxFormField(props: IConditionSelectorFieldProps & BaseFieldProps) {
  return <Field {...props} />;
}
